import { useForm } from "react-hook-form";
import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { createEmployee } from "../lib/api";
import { useAuth } from "../hooks/AuthContext";
import { useState } from "react";
import Swal from "sweetalert2";

// Helper to get nested errors safely
const getNestedError = (obj, path) =>
  path.split(".").reduce((acc, key) => acc?.[key], obj);

// Reusable Input component
const InputField = ({ id, label, register, errors, type = "text", validation,placeholder, ...props }) => {
  const error = getNestedError(errors, id);
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <input
        id={id}
        type={type}
        placeholder={placeholder}
        {...register(id, validation)}
        {...props}
        className={`w-full p-2 border rounded ${error ? "border-red-500" : "border-gray-300"}`}
      />
      {error && <p className="text-red-500 text-xs mt-1">{error.message}</p>}
    </div>
  );
};

// Reusable Select component
const SelectField = ({ id, label, register, errors, options, validation }) => {
  const error = getNestedError(errors, id);
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <select
        id={id}
        {...register(id, validation)}
        className={`w-full p-2 border rounded ${error ? "border-red-500" : "border-gray-300"}`}
      >
        <option value="">Select {label}</option>
        {options.map((opt) => (
          <option key={opt.value || opt} value={opt.value || opt}>{opt.label || opt}</option>
        ))}
      </select>
      {error && <p className="text-red-500 text-xs mt-1">{error.message}</p>}
    </div>
  );
};

// Reusable File Upload
const FileField = ({ id, label, setValue, errors, accept, uploadFn }) => {
  const [uploading, setUploading] = useState(false);
  const error = errors?.[id];

  const handleChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const fileUrl = await uploadFn(file); // upload file and get fileUrl
      setValue(id, fileUrl, { shouldValidate: true }); // store URL in RHF state
    } catch (err) {
      console.error("Upload failed:", err);
      toast.error("File upload failed!");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <input id={id} type="file" accept={accept} onChange={handleChange} className="w-full p-2 border border-gray-300 rounded" />
      {uploading && <p className="text-blue-500 text-xs mt-1">Uploading...</p>}
      {error && <p className="text-red-500 text-xs mt-1">{error.message}</p>}
    </div>
  );
};

function CreateEmployeeForm() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const { register, setValue, handleSubmit, setError, formState: { errors, isDirty }, reset } = useForm({ mode: "onChange" });
  const API_URL = process.env.REACT_APP_API_URL

  // Upload API
  const uploadFile = async (file) => {
    const formData = new FormData();
    formData.append("file", file);

    const res = await fetch(`${API_URL}employees/upload`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`, // keep auth
      },
      body: formData, // file must be FormData
    });

    if (!res.ok) throw new Error("Upload failed");

    const data = await res.json();
    return data.fileUrl;
  };

  // Mutation

  const mutation = useMutation({
    mutationFn: (data) => createEmployee(data, token),
    onSuccess: () => {
      Swal.fire({
        icon: "success",
        title: "Employee created successfully!",
        showConfirmButton: false,
        timer: 2000,
      });
      navigate("/");
      reset();
    },
    onError: (error) => {
      const backendErrors = error.response?.data?.errors;
      if (backendErrors) {
        Object.keys(backendErrors).forEach((field) => {
          Object.keys(backendErrors[field]).forEach((subField) => {
            const path = field + (subField ? `.${subField}` : "");
            setError(path, {
              type: backendErrors[field][subField].type,
              message: backendErrors[field][subField].message,
            });
          });
        });
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: error.response?.data?.message || "Failed to create employee",
        });
      }
    },
  });
  


  const toEpochSeconds = (dateString) => {
    if (!dateString) return null;
    const [year, month, day] = dateString.split("-").map(Number);
    const localDate = new Date(year, month - 1, day, 0, 0, 0);
    return Math.floor(localDate.getTime() / 1000);
  };

  const onSubmit = (data) => {
    const payload = { ...data };
    const dateFields = ["dob", "joiningDate", "previousJobDate"];
    dateFields.forEach((field) => {
      if (payload[field]) {
        const epoch = toEpochSeconds(payload[field]);
        payload[field] = epoch;
      }
    });

    mutation.mutate(payload);
  };

  const handleCancel = () => {
    if (isDirty && !window.confirm("You have unsaved changes. Continue?")) return;
    navigate("/dashboard");
  };

  // Options
  const bloodGroupOptions = ["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"];
  const genderOptions = ["Male", "Female", "Other"];
  const departmentOptions = [ "IT", "HR", "Finance", "Marketing", "Sales", "Operations", "Customer Support", "Administration", "Research & Development", "Relationship Manager", "Legal", "Procurement", "Quality Assurance", "Logistics", "Product Management", "Business Development"];
  const jobTypeOptions = ["full-time", "part-time", "contract", "internship"];
  const accountTypeOptions = ["Savings", "Current"];
  const indianBankOptions = [
    "State Bank of India", "Punjab National Bank", "HDFC Bank", "ICICI Bank", "Axis Bank",
    "Kotak Mahindra Bank", "Bank of Baroda", "Canara Bank", "Union Bank of India", "IDFC FIRST Bank",
    "Yes Bank", "IndusInd Bank", "Bank of India", "Central Bank of India", "Indian Bank", "UCO Bank", "IDBI Bank",
    "Federal Bank", "South Indian Bank", "RBL Bank", "Karur Vysya Bank", "City Union Bank", "Jammu & Kashmir Bank",
    "Dhanlaxmi Bank", "Bandhan Bank", "AU Small Finance Bank", "Equitas Small Finance Bank", "NSDL Payments Bank"
  ];

  return (
    <div className="py-8 px-4">
      <div className="max-w-5xl mx-auto bg-white p-8 rounded-lg shadow-lg">
        <h2 className="text-3xl font-bold mb-8 text-gray-800">Create Employee</h2>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">

          {/* Personal Info */}
          <section>
            <h3 className="text-xl font-semibold mb-4 text-gray-700">Personal Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <InputField id="name" label="Full Name *" register={register} errors={errors} validation={{ required: "Name is required", pattern: { value: /^[A-Za-z\s]+$/, message: "Only alphabets allowed" } }} />
              <InputField id="fatherName" label="Father's Name *" register={register} errors={errors} validation={{ required: "Father's name required", pattern: { value: /^[A-Za-z\s]+$/, message: "Only alphabets" } }} />
              <InputField id="dob" label="Date of Birth *" type="date" register={register} errors={errors} validation={{ required: "DOB is required" }} max={new Date().toISOString().split("T")[0]} />
              <InputField id="designation" label="Designation *" register={register} errors={errors} validation={{ required: "Designation is required" }} />
              <SelectField
                id="department"
                label="Department *"
                register={register}
                errors={errors}
                options={departmentOptions.map(d => ({ value: d, label: d }))}
                validation={{ required: "Department is required" }}
              />
              <SelectField id="bloodGroup" label="Blood Group" register={register} errors={errors} options={bloodGroupOptions.map(b => ({ value: b, label: b }))}  />
              <SelectField id="gender" label="Gender" register={register} errors={errors} options={genderOptions.map(g => ({ value: g, label: g }))} validation={{ required: "Gender required" }} />
              <InputField id="panNo" label="PAN Number *" register={register} errors={errors} validation={{ required: "PAN is required" }} maxLength={10} placeholder="ABCDE1234F" onInput={(e) => {
    e.target.value = e.target.value.toUpperCase();
  }} />
              <InputField id="mobileNo" label="Mobile Number *" register={register} errors={errors} validation={{ required: "Mobile is required", pattern: { value: /^[0-9]{10}$/, message: "Enter 10-digit mobile" } }} maxLength={10} />
              <InputField id="email" label="Email *" type="email" register={register} errors={errors} validation={{ required: "Email is required" }} />
              <InputField id="correspondenceAddress" label="Address *" register={register} errors={errors} validation={{ required: "Address required" }} />
            </div>
          </section>

          {/* Employment Details */}
          <section>
            <h3 className="text-xl font-semibold mb-4 text-gray-700">Employment Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <InputField id="joiningDate" label="Joining Date *" type="date" register={register} errors={errors} validation={{ required: "Joining Date required" }} />
              <SelectField id="jobType" label="Job Type *" register={register} errors={errors} options={jobTypeOptions.map(j => ({ value: j.toLowerCase(), label: j }))} validation={{ required: "Job Type required" }} />
              <InputField id="previousJobName" label="Previous Job Name" register={register} errors={errors} />
              <InputField id="previousJobDate" label="Previous Job End Date" type="date" register={register} errors={errors} />
            </div>
          </section>

          {/* Bank Details */}
          <section>
            <h3 className="text-xl font-semibold mb-4 text-gray-700">Bank Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <SelectField id="bankDetails.bankName" label="Bank Name *" register={register} errors={errors} options={indianBankOptions.map(b => ({ value: b, label: b }))} validation={{ required: "Bank Name required" }} />
              <InputField id="bankDetails.accountNo" label="Account Number *" register={register} errors={errors} validation={{ required: "Account number required", pattern: { value: /^[0-9]+$/, message: "Numbers only" } }} />
              <SelectField id="bankDetails.accountType" label="Account Type *" register={register} errors={errors} options={accountTypeOptions.map(a => ({ value: a.toLowerCase(), label: a }))} validation={{ required: "Account Type required" }} />
              <InputField id="bankDetails.ifscCode" label="IFSC Code *" register={register} errors={errors} validation={{ required: "IFSC Code required" }} maxLength={11} placeholder="ABCD0005943" />
              <InputField id="bankDetails.branch" label="Bank Branch *" register={register} errors={errors} validation={{ required: "Branch is required" }} />

            </div>
          </section>

          {/* File Uploads */}
          <section>
            <h3 className="text-xl font-semibold mb-4 text-gray-700">Documents</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <FileField id="employeePhoto" label="Employee Photo (JPEG/PNG)" accept="image/jpeg,image/png" setValue={setValue} errors={errors} uploadFn={uploadFile} />
              {/* <FileField id="cv" label="CV (PDF)" accept="application/pdf" setValue={setValue} errors={errors} uploadFn={uploadFile} /> */}
              <FileField id="aadharCard" label="Aadhar Card (PDF/Image)" accept="application/pdf,image/jpeg,image/png" setValue={setValue} errors={errors} uploadFn={uploadFile} />
              <FileField id="panCard" label="PAN Card (PDF/Image)" accept="application/pdf,image/jpeg,image/png" setValue={setValue} errors={errors} uploadFn={uploadFile} />
            </div>
          </section>

          {/* Actions */}
          <div className="flex justify-end space-x-4 mt-8">
            <button type="button" onClick={handleCancel} className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300">Cancel</button>
            <button type="submit" disabled={mutation.isPending} className="px-4 py-2 bg-[#2C4E86] text-white rounded hover:bg-blue-900 disabled:bg-blue-300">
              {mutation.isPending ? "Creating..." : "Create Employee"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default CreateEmployeeForm;
